import {sharedCampaigns} from '@cl/shared';
import {CAMPAIGN_NODE_ID, NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {updateCampaignNodes} from 'common/src/modules/campaigns/store/campaignLogic/actions/updateCampaignNodes';
import campaignVersionsApi, {
  PatchTreeParams,
} from 'common/src/modules/campaignVersions/campaignVersions.api';
import {Nodes, NodeType} from 'common/src/modules/nodes/nodes.types';
import getCampaignNodes from 'common/src/modules/nodes/utils/getCampaignNodes';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import refreshCampaignIssues from '../../../utils/refreshCampaignIssues';
import {CardConnectionType} from './cardsConnectionTool.enums';
import {cardsConnectionToolSchema, ConnectionRequest} from './cardsConnectionTool.types';
import collectIssues from './utils/collectIssues';
import {DTCreativeNode} from 'common/src/modules/decisionTree/decisionTree.types';

function cardsConnectionTool(store: CLStore) {
  return tool({
    name: CampaignSetupAgentToolName.CardsConnection,
    description: 'Connect or disconnect cards in the campaign decision tree',
    displayName: 'Updating map connections',
    inputSchema: cardsConnectionToolSchema,
    execute: async ({
      input: {connections},
    }: ToolExecuteParams<typeof cardsConnectionToolSchema>) => {
      const state = store.getState();
      const campaignLogics = state.campaignPage.campaignLogic;

      if (!campaignLogics?.nodes) {
        return toolResult(
          JSON.stringify({
            success: false,
            message: 'No campaign setup data available',
          }),
        );
      }

      const storeNodes = sharedCampaigns.nodesRef.value;

      // Collect issues for the connections
      const issues = collectIssues(connections as ConnectionRequest[], storeNodes);

      if (issues.length > 0) {
        return toolResult(
          JSON.stringify({
            success: false,
            message: `Had some issues:\n${issues.map((issue) => `* ${issue}`).join('\n')}`,
          }),
        );
      }

      const campaign = state.campaignPage?.campaignTree?.campaign;
      const versionNumber = state.campaignPage?.campaignTree?.versionNumber;

      if (!campaign || !versionNumber) {
        return toolResult(
          JSON.stringify({
            success: false,
            message: 'Campaign or version number not found in state',
          }),
        );
      }

      // Separate campaign connections from regular card connections
      const campaignConnections = connections.filter(
        (connection) => connection.sourceCardId === CAMPAIGN_NODE_ID,
      );
      const cardConnections = connections.filter(
        (connection) => connection.sourceCardId !== CAMPAIGN_NODE_ID,
      );

      const nodesToUpdate: Nodes = {};

      // Process regular card-to-card connections
      for (const connection of cardConnections) {
        nodesToUpdate[connection.sourceCardId] = storeNodes[connection.sourceCardId];
        const existingNode = storeNodes[connection.sourceCardId];
        const currentOutput = existingNode.outputs.find(
          (output) => output.id === connection.sourceCardOutputId,
        );

        if (!currentOutput) continue;

        const destinationNode = storeNodes[connection.targetCardId];

        if (!destinationNode) {
          return toolResult(
            JSON.stringify({
              success: false,
              message: `Target card not found in state`,
            }),
          );
        }

        // Check if the destination node is a fallback creative
        if (
          destinationNode.type === NodeType.CREATIVE &&
          (destinationNode as DTCreativeNode).isFallbackCreative
        ) {
          return toolResult(
            JSON.stringify({
              success: false,
              message: `Cannot connect a fallback creative to another card`,
            }),
          );
        }

        currentOutput.nodeId =
          connection.type === CardConnectionType.Connect ? connection.targetCardId : NO_NODE_ID;
      }

      if (Object.keys(nodesToUpdate).length === 0 && campaignConnections.length === 0) {
        return toolResult(
          JSON.stringify({
            success: false,
            message: 'No nodes to update',
          }),
        );
      }

      // Handle campaign card connections - determine final rootNodeId
      let rootNodeId = NO_NODE_ID;

      // Process campaign connections to determine the final state
      // If there are both disconnect and connect operations, process them in order
      for (const connection of campaignConnections) {
        if (connection.type === CardConnectionType.Disconnect) {
          // Disconnect: set rootNodeId to NO_NODE_ID (unless overridden by a later connect)
          if (rootNodeId === connection.targetCardId) {
            rootNodeId = NO_NODE_ID;
          }
        } else if (connection.type === CardConnectionType.Connect) {
          // Connect: validate target exists and set as new root
          const targetNode = storeNodes[connection.targetCardId];

          if (!targetNode) {
            return toolResult(
              JSON.stringify({
                success: false,
                message: `Target card ${connection.targetCardId} not found`,
              }),
            );
          }

          // Check if trying to connect to a fallback creative
          if (
            targetNode.type === NodeType.CREATIVE &&
            (targetNode as DTCreativeNode).isFallbackCreative
          ) {
            return toolResult(
              JSON.stringify({
                success: false,
                message: `Cannot connect campaign to a fallback creative`,
              }),
            );
          }

          rootNodeId = connection.targetCardId;
        }
      }

      // Call API to update the nodes (similar to updateTree)
      const basePayload: PatchTreeParams = {
        campaignId: campaign.id,
        versionNumber: versionNumber,
        nodesToAdd: [],
        nodesToDelete: [],
        nodesToUpdate: nodesToUpdate as Nodes,
        campaignNode: campaign,
        ...(campaignConnections.length > 0 && {rootNodeId}), //set the root node id if any of the connections affect the campaign node
      };

      const response = await campaignVersionsApi.updateTree(basePayload);

      // Get updated nodes from response and update nodesRef
      const newNodes = getCampaignNodes(response);

      store.dispatch(
        updateCampaignNodes({
          connectedNodes: newNodes,
          campaignTreeType: CampaignTreeType.Campaign,
        }),
      );

      // Refresh campaign issues after connection changes
      await refreshCampaignIssues(store);

      return toolResult(
        JSON.stringify({
          success: true,
          message: 'Success',
        }),
      );
    },
  });
}

export default cardsConnectionTool;
